import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Divider,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@material-ui/core';
import { useStyles } from './Interfaces.style';
import DicomInterfaces from './Dicom/DicomInterfaces';
import WorklistInterfaces from './Worklist/WorklistInterfaces';
import RisInterfaces from './Ris/RisInterfaces';
import SelectWithOther from './Common/SelectWithOther';

const Interfaces = ({
  priors_fetching,
  query_worklist,
  settings,
  report,
  updateSettingsCheckbox,
  updateSettingsValue,
  setReportRulesChanged,
  updateReportField,
  updateReportFieldValue,
  updateReportFieldCheckbox,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'Français' },
    { value: 'de', label: 'Deutsch' },
    { value: 'es', label: 'Español' },
    { value: 'lt', label: 'Lithuanian' },
    { value: 'pl', label: 'Polski' },
    { value: 'it', label: 'Italiano' },
  ];
  const handleCheckboxChange = useCallback(
    (event) => updateSettingsCheckbox(event),
    [updateSettingsCheckbox],
  );

  return (
    <>
      {/* Reports section */}
      <Card elevation={1} className={classes.card_split_container}>
        <div className={classes.reports_section_container}>
          <div className={classes.header_container}>
            <Typography
              variant='h6'
              color='primary'
              className={`${classes.titleSettingsCard} ${classes.title_margin}`}>
              {t('Settings.send_reports')}
            </Typography>
          </div>
          <div className={classes.report_settings_container}>
            {/* hide operator name */}
            <div className={classes.report_options_container}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!report.hide_operator_name}
                    onChange={updateReportFieldCheckbox}
                    name='hide_operator_name'
                  />
                }
                label={t('Settings.hide_operator_name')}
              />
            </div>

            {/* image compression */}
            <div className={classes.report_options_container}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!report.image_compression}
                    onChange={updateReportFieldCheckbox}
                    name='image_compression'
                  />
                }
                label={t('Settings.compress_image_report')}
              />
            </div>

            {/* dark report theme */}
            <div className={classes.report_options_container}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!report.dark_mode}
                    onChange={updateReportFieldCheckbox}
                    name='dark_mode'
                  />
                }
                label={t('Settings.dark_report_theme')}
              />
            </div>

            {/* priors report */}
            <div className={classes.report_options_container}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!settings.priors_report}
                    onChange={handleCheckboxChange}
                    name='priors_report'
                  />
                }
                label={t('Settings.priors_report')}
              />
            </div>

            {/* report delay */}
            <div className={classes.report_options_container}>
              <TextField
                label={t('Settings.delay')}
                fullWidth={false}
                value={report.sending_delay}
                onChange={updateReportFieldValue}
                name='sending_delay'
                className={`${classes.report_settings_input} ${classes.report_delay_input}`}
                size='small'
                variant='outlined'
                autoComplete='off'
              />
            </div>

            {/* Set language */}
            <div className={classes.report_options_container}>
              <FormControl fullWidth>
                <InputLabel id='language-label'>{t('Settings.report_language')}</InputLabel>
                <Select
                  labelId='language-label'
                  name='language'
                  value={report.language}
                  onChange={(e) => updateReportField(e.target.name, e.target.value)}>
                  {languageOptions.map((option, i) => (
                    <MenuItem className={classes.radio_label_option} value={option.value} key={i}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>

          <div className={classes.routing_tag_section}>
            <div className={classes.routing_tag_row}>
              <FormControl variant='outlined' size='small' className={classes.routing_tag_kind}>
                <InputLabel id='routing-tag-kind-label'>{t('Settings.routing_tag')}</InputLabel>
                <Select
                  labelId='routing-tag-kind-label'
                  name='routing_tag_kind'
                  value={report.routing_tag_kind || ''}
                  onChange={(e) => updateReportField(e.target.name, e.target.value)}
                  label={t('Settings.routing_tag')}>
                  <MenuItem value=''>{t('Settings.none')}</MenuItem>
                  <MenuItem value='standard'>{t('Settings.routing_tag_standard')}</MenuItem>
                  <MenuItem value='private'>{t('Settings.routing_tag_private')}</MenuItem>
                </Select>
              </FormControl>
              {report.routing_tag_kind === 'standard' && (
                <TextField
                  label={t('Settings.routing_tag_keyword')}
                  placeholder={t('Settings.routing_tag_keyword_placeholder')}
                  value={report.routing_tag || ''}
                  onChange={(e) => updateReportField(e.target.name, e.target.value)}
                  name='routing_tag'
                  size='small'
                  variant='outlined'
                  autoComplete='off'
                  className={classes.routing_tag_field}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 64 }}
                />
              )}
            </div>
            {report.routing_tag_kind === 'private' && (
              <div className={classes.routing_tag_row}>
                <TextField
                  label={t('Settings.routing_tag_group')}
                  placeholder='0x0019'
                  value={report.routing_tag_group || ''}
                  onChange={(e) => updateReportField(e.target.name, e.target.value)}
                  name='routing_tag_group'
                  size='small'
                  variant='outlined'
                  autoComplete='off'
                  className={classes.routing_tag_field}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 6 }}
                />
                <TextField
                  label={t('Settings.routing_tag_creator')}
                  placeholder='CREATOR'
                  value={report.routing_tag_creator || ''}
                  onChange={(e) => updateReportField(e.target.name, e.target.value)}
                  name='routing_tag_creator'
                  size='small'
                  variant='outlined'
                  autoComplete='off'
                  className={classes.routing_tag_field}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 64 }}
                />
                <TextField
                  label={t('Settings.routing_tag_offset')}
                  placeholder='0x10'
                  value={report.routing_tag_offset || ''}
                  onChange={(e) => updateReportField(e.target.name, e.target.value)}
                  name='routing_tag_offset'
                  size='small'
                  variant='outlined'
                  autoComplete='off'
                  className={classes.routing_tag_field}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 4 }}
                />
              </div>
            )}
            <Typography variant='caption' className={classes.routing_tag_helper}>
              {t('Settings.routing_tag_helper')}
            </Typography>
          </div>
        </div>
      </Card>
      {/* Priors section */}
      {(priors_fetching || query_worklist) && (
        <Card elevation={1} className={classes.card_split_container}>
          <div className={classes.priors_section_container}>
            <div className={classes.header_container}>
              <Typography
                variant='h6'
                color='primary'
                className={`${classes.titleSettingsCard} ${classes.title_margin}`}>
                {t('Settings.priors_settings')}
              </Typography>
            </div>
            {/* Prior limit */}
            <div className={classes.priors_limits_container}>
              {/* Year limit */}
              <div className={classes.priors_limits_container_item}>
                <SelectWithOther
                  label_explanation={t('Settings.prior_year_limit_explanation')}
                  label={t('Settings.prior_year_limit')}
                  value={settings.priors_years_limit}
                  min={1}
                  max={5}
                  onChange={(val) =>
                    updateSettingsValue({
                      target: { name: 'priors_years_limit', value: val },
                    })
                  }
                />
              </div>
              <Divider orientation='vertical' flexItem />
              {/* Number of studies limit */}
              <div className={classes.priors_limits_container_item}>
                <SelectWithOther
                  label_explanation={t('Settings.prior_limit_explanation')}
                  label={t('Settings.prior_limit')}
                  value={settings.priors_number_limit}
                  min={1}
                  max={5}
                  onChange={(val) =>
                    updateSettingsValue({
                      target: { name: 'priors_number_limit', value: val },
                    })
                  }
                />
              </div>
            </div>
          </div>
        </Card>
      )}
      {/* Interfaces section */}
      <DicomInterfaces setReportRulesChanged={setReportRulesChanged} />
      {query_worklist && <WorklistInterfaces setReportRulesChanged={setReportRulesChanged} />}{' '}
      <RisInterfaces setReportRulesChanged={setReportRulesChanged} />
    </>
  );
};

export default Interfaces;
